/**
 * IMP-177 / SEC-006 후속: IDOR 감사 가드 모듈 (SSoT)
 *
 * 왜 이 모듈이 SSoT 인가:
 *   - SEC-006(Phase 2) FE defense-in-depth 가드가 DealerIssuePanel·LicenseDealerTab·
 *     SmartDentAction 에 분산돼 있었으나, 관측 가능성(audit) 은 없었다.
 *   - IMP-177 목표: mismatch 발생 시 console.warn + Sentry breadcrumb 을 일관되게
 *     발화하여 silent-drop 금지(BUG-119 회귀 방지) 원칙을 준수한다.
 *   - 모든 IDOR 가드 호출은 이 모듈의 함수를 경유한다. 인라인 비교 로직 금지.
 *
 * no-silent-drop 준수:
 *   모든 drop/block 발생 시 auditIdorBlock 을 통해 console.warn + Sentry breadcrumb
 *   을 반드시 동반한다. 단, 로딩 중(record=null) 등 정상 대기 상태는 발화 제외.
 */

import { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { extractDealerIdFromSkStrict } from './licenseHelper';

// ─── maskId ──────────────────────────────────────────────────────────────────

/**
 * PII 마스킹: id 원본을 로그에 노출하지 않기 위해 앞 일부만 남기고 가린다.
 * 비문자열·빈값 → '(empty)', 길이>4 → `${앞4자}***(길이)`, 1~4 → `${첫문자}***`.
 */
export const maskId = (id) => {
    if (typeof id !== 'string' || !id) return '(empty)';
    if (id.length > 4) return `${id.slice(0, 4)}***(${id.length})`;
    return `${id[0]}***`;
};

// ─── auditIdorBlock ───────────────────────────────────────────────────────────

/**
 * IDOR 가드가 차단을 결정할 때 반드시 호출한다.
 * console.warn + Sentry addBreadcrumb 을 통해 관측 가능성을 보장한다.
 * payload 에는 maskId 가 적용된 값만 포함 — 원본 id 노출 금지(CWE-200 defense-in-depth).
 *
 * @param {{ guard: string, expectedId: string|null, actualId: string|null, reason: string }} params
 */
export const auditIdorBlock = ({ guard, expectedId, actualId, reason }) => {
    const payload = {
        guard,
        expectedId: maskId(expectedId),
        actualId: maskId(actualId),
        reason,
    };
    // eslint-disable-next-line no-console
    console.warn('[idor-guard] blocked', payload);
    Sentry.addBreadcrumb({
        category: 'security.idor-guard',
        level: 'warning',
        message: `[idor-guard] ${guard} blocked: ${reason}`,
        data: payload,
    });
};

// ─── buildAllowedDealerIds ───────────────────────────────────────────────────

/**
 * dealerAllocations 배열에서 strict sk 추출이 성공하는 dealerId 만 모아 Set 으로 반환.
 * extractDealerIdFromSkStrict 실패(falsy) 항목은 제외(포맷 검증 통과 보장).
 *
 * @param {Array} dealerAllocations
 * @returns {Set<string>}
 */
export const buildAllowedDealerIds = (dealerAllocations) => {
    const ids = new Set();
    (dealerAllocations || []).forEach((d) => {
        const id = extractDealerIdFromSkStrict(d?.sk);
        if (id) ids.add(id);
    });
    return ids;
};

// ─── filterRecordsByOwnerId ──────────────────────────────────────────────────

/**
 * 응답 레코드 배열에서 owner mismatch 항목을 drop 하고, drop 이 1건 이상이면
 * auditIdorBlock 을 1회(집계) 발화한다. 건별 warn 금지(noise 방지).
 *
 * fail-open: owner 가 falsy(부재) 인 레코드는 keep — 정규화 미완 레코드 drop 회귀 방지.
 *
 * @param {{ records: Array, expectedId: string, getOwnerId: function, guard: string }} params
 * @returns {Array}
 */
export const filterRecordsByOwnerId = ({ records, expectedId, getOwnerId, guard }) => {
    let droppedCount = 0;
    const filtered = (records || []).reduce((acc, r) => {
        const owner = getOwnerId(r);
        if (!owner) {
            // fail-open: owner 부재 → keep
            acc.push(r);
        } else if (owner === expectedId) {
            acc.push(r);
        } else {
            droppedCount++;
        }
        return acc;
    }, []);

    if (droppedCount > 0) {
        // 집계 1회만 — 건별 원본 dealerId 를 로그에 노출하지 않는다(CWE-200).
        // actualId: null — maskId(null) → '(empty)' 로 명확히 표시, 건수는 reason 에 포함.
        auditIdorBlock({
            guard,
            expectedId,
            actualId: null,
            reason: `response owner mismatch — ${droppedCount} record(s) dropped`,
        });
    }

    return filtered;
};

// ─── useResourceOwnershipGuard ───────────────────────────────────────────────

/**
 * 단일 record 의 소유권을 검증하는 훅.
 * record 가 null(로딩 중)이면 audit 미발화 — 로딩 윈도우 오탐 제외.
 * id 또는 record 가 falsy 여도 audit 미발화.
 *
 * @param {{ id: string|null, record: object|null, extractIdFromRecord: function, guard: string }} params
 * @returns {boolean}
 */
export const useResourceOwnershipGuard = ({ id, record, extractIdFromRecord, guard }) => {
    const ownerId = useMemo(
        () => (record ? extractIdFromRecord(record) : null),
        [record, extractIdFromRecord],
    );
    const allowed = !!id && !!record && ownerId === id;

    useEffect(() => {
        if (id && record && !allowed) {
            auditIdorBlock({
                guard: guard || 'resource-ownership',
                expectedId: id,
                actualId: ownerId,
                reason: 'record owner mismatch',
            });
        }
    }, [id, record, allowed, ownerId, guard]);

    return allowed;
};

// ─── useDealerIdGuard ────────────────────────────────────────────────────────

// 모듈 레벨 상수 — useResourceOwnershipGuard 의 useMemo deps 안정화.
// 인라인 화살표 대신 상수 참조를 전달하여 매 렌더 새 참조 생성 방지.
const extractDealerIdFromAllocation = (a) => extractDealerIdFromSkStrict(a?.sk);

/**
 * dealerAllocation.sk 에서 strict 추출한 dealerId 와 props.dealerId 를 비교한다.
 * mismatch 시 auditIdorBlock(security.idor-guard breadcrumb) 자동 발화.
 *
 * DealerIssuePanel SEC-006 가드의 audit 동반 버전.
 *
 * @param {string|null} dealerId
 * @param {object|null} dealerAllocation
 * @returns {boolean}
 */
export const useDealerIdGuard = (dealerId, dealerAllocation) =>
    useResourceOwnershipGuard({
        id: dealerId,
        record: dealerAllocation,
        extractIdFromRecord: extractDealerIdFromAllocation,
        guard: 'smartdent.dealer-allocation',
    });

// ─── useDealerIdAllowedInAllocations ─────────────────────────────────────────

/**
 * selectedDealerId 가 dealerAllocations 집합 안에 있는지 검증한다.
 * allocations 가 비어있으면(로딩 중) audit 미발화.
 * selectedDealerId 가 falsy 이면 audit 미발화.
 *
 * LicenseDealerTab SEC-006 가드의 audit 동반 버전.
 *
 * BUG-200: self(자기 자신)는 dealerAllocations 집합에 정상적으로 존재하지 않는다
 * (자기 자신에게는 배분 레코드가 없음) — selfId 를 전달하면 selectedDealerId===selfId
 * 일 때 allowed=true 로 취급해 오탐 audit 발화를 막는다.
 *
 * @param {string|null} selectedDealerId
 * @param {Array} dealerAllocations
 * @param {string|null} [selfId]
 * @returns {boolean}
 */
export const useDealerIdAllowedInAllocations = (selectedDealerId, dealerAllocations, selfId = null) => {
    const allowedSet = useMemo(
        () => buildAllowedDealerIds(dealerAllocations),
        [dealerAllocations],
    );
    const isSelf = !!selectedDealerId && selectedDealerId === selfId;
    const allowed = isSelf || (!!selectedDealerId && allowedSet.has(selectedDealerId));
    const loaded = Array.isArray(dealerAllocations) && dealerAllocations.length > 0;

    // deps 에 allowedSet 불요: effect 본문이 allowedSet 을 직접 참조하지 않고,
    // allowed(= allowedSet.has(selectedDealerId)) 가 파생값이므로 allocations 변경 시
    // allowedSet → allowed 재계산 → deps 의 allowed 변화로 재실행된다. stale closure 없음
    // (exhaustive-deps 도 본문 미참조 변수는 요구하지 않음). — IMP-177 PR #377 review m3
    useEffect(() => {
        if (selectedDealerId && loaded && !allowed) {
            auditIdorBlock({
                guard: 'smartdent.dealer-allocation-set',
                expectedId: selectedDealerId,
                actualId: null,
                reason: 'selected dealerId not in allocation set',
            });
        }
    }, [selectedDealerId, loaded, allowed]);

    return allowed;
};
